// Errors whose text is meant for the user. i18next-free and safe in candidate
// builds: the thrower supplies the translation (usually a `t` call), so this
// module never needs a namespace.

/**
 * Renders the error text: `'en'` for the English kept as `Error.message`
 * (logs), `undefined` for the display language. Typically
 * `(lng) => t('some.key', { ...params, lng })`. Null means "no text of its
 * own": the caller's fallback shows.
 */
export type ErrorTextRenderer = (lng: 'en' | undefined) => string | null;

const render = (
  renderer: ErrorTextRenderer,
  lng: 'en' | undefined
): string | null => {
  try {
    return renderer(lng) || null;
  } catch (error) {
    // swallow-ok: a broken renderer must not mask the original failure; the caller's fallback shows
    console.error('[TranslatableError] could not render error text', error);
    return null;
  }
};

/**
 * An error the user may see, in the display language. `extractErrorMessage`
 * and `getApiErrorText` (utils/apiError) show `displayText()`; every other
 * thrown `Error` shows the caller's translated fallback instead of its
 * (English) message. `message` holds the English for logs.
 */
export class TranslatableError extends Error {
  private readonly renderer: ErrorTextRenderer;

  constructor(renderer: ErrorTextRenderer, options?: ErrorOptions) {
    super(render(renderer, 'en') ?? '', options);
    this.name = 'TranslatableError';
    this.renderer = renderer;
  }

  /** The text in the display language, or null to show the caller's fallback. */
  displayText(): string | null {
    return render(this.renderer, undefined);
  }
}

/**
 * Text already rendered for display (e.g. an `extractErrorMessage` result a
 * hook rethrows), so callers that catch the rethrow show it unchanged.
 */
export const displayTextError = (
  text: string,
  options?: ErrorOptions
): TranslatableError => new TranslatableError(() => text, options);

/** The display text of a `TranslatableError`, else null. */
export const translatableErrorText = (error: unknown): string | null =>
  error instanceof TranslatableError ? error.displayText() : null;
